import {
    Badge,
    Group,
    Text,
    Tooltip,
} from '@mantine/core';
import {AiOutlineClockCircle} from 'react-icons/ai';

import {
    SyntheticUser,
    SyntheticUserLifecycleStage,
    SyntheticUserState,
} from '../types';
import {isOverdue} from '../utils/sortByNextWake';
import {formatTimestamp} from '../../../utils/TableTimestamps.tsx';

// FNP-848: a TOMBSTONED row outlives the account it describes. It is a legitimate,
// meaningful record — not an error and not a leftover — so it gets a clear,
// deliberate treatment rather than being hidden or left looking broken.
const STATE_DISPLAY: Record<SyntheticUserState, { color: string, label: string, description: string }> = {
    [SyntheticUserState.AWAKE]: {
        color: 'green',
        label: 'Awake',
        description: 'Active in the fleet and scheduled to wake.',
    },
    [SyntheticUserState.PAUSED]: {
        color: 'yellow',
        label: 'Paused',
        description: 'Will not wake until resumed.',
    },
    [SyntheticUserState.TOMBSTONED]: {
        color: 'dark',
        label: 'Tombstoned',
        description: 'The underlying account is gone; this row is the surviving record that the synthetic user existed. Retained on purpose — it is what distinguishes synthetic activity from organic activity.',
    },
};

export const SyntheticUserStateBadge = ({state}: { state?: SyntheticUserState }) => {
    if (!state) {
        return <Badge color={'dark'} variant={'outline'}>Unknown</Badge>;
    }

    const display = STATE_DISPLAY[state];
    if (!display) {
        return <Badge color={'dark'}>{state}</Badge>;
    }

    return (
        <Tooltip label={display.description} multiline w={300} withArrow>
            <Badge
                color={display.color}
                // Tombstoned is outlined rather than filled: clearly marked and fully
                // legible, but visually distinct from a live row so a roster of
                // tombstones never reads as a wall of active users.
                variant={state === SyntheticUserState.TOMBSTONED ? 'outline' : 'light'}
            >
                {display.label}
            </Badge>
        </Tooltip>
    );
};

const LIFECYCLE_DISPLAY: Record<SyntheticUserLifecycleStage, { color: string, label: string }> = {
    [SyntheticUserLifecycleStage.PROVISIONING]: {
        color: 'blue',
        label: 'Provisioning'
    },
    [SyntheticUserLifecycleStage.ACTIVE]: {
        color: 'green',
        label: 'Active'
    },
    [SyntheticUserLifecycleStage.LAPSING]: {
        color: 'orange',
        label: 'Lapsing'
    },
    [SyntheticUserLifecycleStage.CANCELLED]: {
        color: 'red',
        label: 'Cancelled'
    },
    [SyntheticUserLifecycleStage.DELETED]: {
        color: 'gray',
        label: 'Deleted'
    },
};

export const LifecycleStageBadge = ({stage}: { stage?: SyntheticUserLifecycleStage }) => {
    if (!stage) {
        return <Badge color={'dark'} variant={'outline'}>Unknown</Badge>;
    }

    const display = LIFECYCLE_DISPLAY[stage];
    if (!display) {
        return <Badge color={'dark'}>{stage}</Badge>;
    }

    return <Badge color={display.color} variant={'light'}>{display.label}</Badge>;
};

// nextWakeAt is the operational field, so it renders as a cell that states its own
// meaning: a real time, "overdue" when an awake user's wake time has passed, or an
// explicit reason there is no wake — never a bare blank.
export const NextWakeCell = ({record}: { record: SyntheticUser }) => {

    if (!record.nextWakeAt) {
        if (record.state === SyntheticUserState.TOMBSTONED) {
            return <Text size={'sm'} c={'dimmed'}>Never (tombstoned)</Text>;
        }
        if (record.state === SyntheticUserState.PAUSED) {
            return <Text size={'sm'} c={'dimmed'}>Not while paused</Text>;
        }
        // An AWAKE user with no scheduled wake is anomalous — say so rather than
        // rendering an empty cell that reads as "nothing to see here".
        return (
            <Tooltip label={'This user is awake but has no scheduled wake time.'} withArrow>
                <Text size={'sm'} c={'orange'}>Not scheduled</Text>
            </Tooltip>
        );
    }

    const overdue = isOverdue(record);

    return (
        <Group gap={4} wrap={'nowrap'}>
            {overdue && (
                <Tooltip label={'Wake time has passed — the scheduler may be stalled.'} withArrow>
                    <Text c={'orange'} span><AiOutlineClockCircle/></Text>
                </Tooltip>
            )}
            <Text size={'sm'} c={overdue ? 'orange' : undefined}>
                {formatTimestamp(record.nextWakeAt)}
            </Text>
        </Group>
    );
};
